import { useMemo, useState } from 'react';
import type { Book } from '../lib/api/types';

interface AtlasView2DProps {
  books: Book[];
  onSelect: (slug: string) => void;
}

// 分类 → 色调
const CATEGORY_COLOR: Record<string, string> = {
  勇气: '#4ade80',
  冒险: '#fbbf24',
  友谊: '#60a5fa',
  童话: '#a78bfa',
  科学: '#22d3ee',
  历史: '#f472b6',
  自然: '#34d399',
};
const DEFAULT_COLOR = '#8ab4ff';

function categoryColor(category?: string): string {
  if (!category) return DEFAULT_COLOR;
  return CATEGORY_COLOR[category] ?? DEFAULT_COLOR;
}

// 页数 → 星球大小（SVG 半径 1.8–4.2）
function pageRadius(pagesCount: number): number {
  return Math.min(4.2, Math.max(1.8, 1.8 + pagesCount * 0.22));
}

/**
 * Atlas 2D 星图增强（WebGL 降级版 · Ticket 02）：
 * SVG 星空 + 椭圆轨道 + 分类着色 + 页数大小 + 免费书光环 + hover tooltip。
 * 无 GPU 依赖，沙箱实测可用。
 */
export function AtlasView2D({ books, onSelect }: AtlasView2DProps) {
  const [hoveredSlug, setHoveredSlug] = useState<string | null>(null);

  // 固定种子生成星空
  const stars = useMemo(() => {
    const rand = (seed: number) => {
      const x = Math.sin(seed * 9301 + 49297) * 233280;
      return x - Math.floor(x / 1) * 1;
    };
    return Array.from({ length: 140 }, (_, i) => ({
      cx: 6 + rand(i + 1) * 94,
      cy: 4 + rand(i + 101) * 92,
      r: 0.3 + rand(i + 7) * 1.1,
      o: 0.25 + rand(i + 31) * 0.6,
      tw: 2 + (i % 5),
    }));
  }, []);

  // 环形布局（椭圆轨道）
  const nodes = useMemo(
    () =>
      books.map((book, i) => {
        const angle = (i / Math.max(1, books.length)) * Math.PI * 2 - Math.PI / 2;
        const rx = 38;
        const ry = 22;
        const cx = 50 + Math.cos(angle) * rx;
        const cy = 50 + Math.sin(angle) * ry;
        const color = categoryColor(book.category);
        const r = pageRadius(book.pages_count || 5);
        return {
          book,
          x: cx,
          y: cy,
          color,
          r,
          isHovered: hoveredSlug === book.slug,
        };
      }),
    [books, hoveredSlug],
  );

  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', background: 'radial-gradient(ellipse at 50% 40%, #1b2350 0%, #0d1030 55%, #07091c 100%)' }}>
      <svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice" style={{ width: '100%', height: '100%' }}>
        <defs>
          <radialGradient id="nebula1" cx="30%" cy="28%" r="45%">
            <stop offset="0%" stopColor="#4ade8044" />
            <stop offset="100%" stopColor="#4ade8000" />
          </radialGradient>
          <radialGradient id="nebula2" cx="72%" cy="70%" r="50%">
            <stop offset="0%" stopColor="#7c4dff3d" />
            <stop offset="100%" stopColor="#7c4dff00" />
          </radialGradient>
          {nodes.map((n) => (
            <radialGradient key={n.book.id} id={`glow-${n.book.id}`} cx="35%" cy="30%" r="55%">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="0.8" />
              <stop offset="45%" stopColor={n.color} stopOpacity="0.55" />
              <stop offset="100%" stopColor={n.color} stopOpacity="0" />
            </radialGradient>
          ))}
        </defs>
        <rect x="0" y="0" width="100" height="100" fill="url(#nebula1)" />
        <rect x="0" y="0" width="100" height="100" fill="url(#nebula2)" />

        {/* 背景星点 */}
        {stars.map((s, i) => (
          <circle key={i} cx={s.cx} cy={s.cy} r={s.r} fill="#fff" opacity={s.o}>
            <animate attributeName="opacity" values={`${s.o};${s.o * 0.3};${s.o}`} dur={`${s.tw}s`} repeatCount="indefinite" />
          </circle>
        ))}

        {/* 轨道 */}
        <ellipse cx="50" cy="50" rx="40" ry="24" fill="none" stroke="#ffffff22" strokeWidth="0.25" strokeDasharray="0.5 1.2" />
        <ellipse cx="50" cy="50" rx="30" ry="17" fill="none" stroke="#ffffff14" strokeWidth="0.2" />

        {/* 节点连线 */}
        {nodes.length > 1 &&
          nodes.map((n, i) => {
            const next = nodes[(i + 1) % nodes.length];
            if (!next) return null;
            return (
              <line
                key={`line-${i}`}
                x1={n.x}
                y1={n.y}
                x2={next.x}
                y2={next.y}
                stroke={n.color}
                strokeWidth="0.12"
                opacity="0.28"
              />
            );
          })}

        {/* 书籍星球节点 */}
        {nodes.map((n) => {
          const hovered = n.isHovered;
          const scale = hovered ? 1.3 : 1;
          const outerR = n.r * scale;
          return (
            <g
              key={n.book.id}
              style={{ cursor: 'pointer' }}
              onClick={() => onSelect(n.book.slug)}
              onMouseEnter={() => setHoveredSlug(n.book.slug)}
              onMouseLeave={() => setHoveredSlug(null)}
            >
              {/* 辉光 */}
              <circle cx={n.x} cy={n.y} r={outerR * 2.2} fill={`url(#glow-${n.book.id})`} opacity={hovered ? 0.9 : 0.55}>
                <animate attributeName="r" values={`${outerR * 1.8};${outerR * 2.5};${outerR * 1.8}`} dur={`${3 + n.r * 0.4}s`} repeatCount="indefinite" />
              </circle>

              {/* 免费书：多层光环 */}
              {n.book.is_free && (
                <>
                  <circle cx={n.x} cy={n.y} r={outerR * 1.7} fill="none" stroke={n.color} strokeWidth="0.3" opacity={hovered ? 0.55 : 0.22}>
                    <animate attributeName="r" values={`${outerR * 1.5};${outerR * 1.9};${outerR * 1.5}`} dur="4s" repeatCount="indefinite" />
                  </circle>
                  <circle cx={n.x} cy={n.y} r={outerR * 2.0} fill="none" stroke="#ffffff" strokeWidth="0.15" opacity={hovered ? 0.3 : 0.12}>
                    <animate attributeName="r" values={`${outerR * 1.8};${outerR * 2.2};${outerR * 1.8}`} dur="5s" repeatCount="indefinite" />
                  </circle>
                </>
              )}

              {/* 付费书：条纹环 */}
              {!n.book.is_free && (
                <circle cx={n.x} cy={n.y} r={outerR * 1.55} fill="none" stroke={n.color} strokeWidth="0.4" opacity={hovered ? 0.6 : 0.28} strokeDasharray={`${outerR * 0.4} ${outerR * 0.7}`}>
                  <animateTransform attributeName="transform" type="rotate" values={`0 ${n.x} ${n.y};360 ${n.x} ${n.y}`} dur="20s" repeatCount="indefinite" />
                </circle>
              )}

              {/* 星球主体 */}
              <circle cx={n.x} cy={n.y} r={outerR} fill={n.color} />
              <circle cx={n.x - outerR * 0.35} cy={n.y - outerR * 0.38} r={outerR * 0.35} fill="#ffffff" opacity="0.75" />
              <circle cx={n.x + outerR * 0.25} cy={n.y + outerR * 0.3} r={outerR * 0.22} fill="#000000" opacity="0.12" />

              {/* 缓慢旋转（整组） */}
              <animateTransform attributeName="transform" type="rotate" values={`0 ${n.x} ${n.y};360 ${n.x} ${n.y}`} dur={`${12 + n.r * 1.5}s`} repeatCount="indefinite" />
            </g>
          );
        })}
      </svg>

      {/* Hover Tooltip（DOM 层，比 SVG text 更灵活） */}
      {hoveredSlug && (() => {
        const node = nodes.find((n) => n.book.slug === hoveredSlug);
        if (!node) return null;
        return (
          <div
            style={{
              position: 'absolute',
              left: `${node.x}%`,
              top: `${Math.max(5, node.y - 18)}%`,
              transform: 'translate(-50%, 0)',
              background: 'rgba(10,12,28,0.92)',
              border: `1.5px solid ${node.color}`,
              color: '#fff',
              padding: '12px 16px',
              borderRadius: '14px',
              fontSize: '13px',
              maxWidth: '260px',
              boxShadow: `0 0 28px ${node.color}55`,
              fontFamily: 'system-ui, sans-serif',
              lineHeight: 1.6,
              pointerEvents: 'none',
              zIndex: 60,
              transition: 'opacity 0.18s ease',
            }}
          >
            <div style={{ fontWeight: 700, fontSize: '15px', marginBottom: '6px', color: node.color }}>{node.book.title}</div>
            <div style={{ opacity: 0.8, marginBottom: '6px' }}>
              {node.book.category || '童话'} · {node.book.pages_count || '?'} 页 · {node.book.is_free ? '免费' : `¥${(node.book.price_cents / 100).toFixed(0)}`}
            </div>
            <div style={{ opacity: 0.55, fontSize: '12px' }}>
              {node.book.description?.slice(0, 70) || 'AI 生成的互动绘本'}
              {(node.book.description?.length ?? 0) > 70 ? '…' : ''}
            </div>
          </div>
        );
      })()}

      {/* 底部提示 */}
      <div style={{ position: 'absolute', bottom: '34px', left: '50%', transform: 'translateX(-50%)', textAlign: 'center' }}>
        <div style={{ color: '#ffffff90', fontSize: '13px', letterSpacing: '2px' }}>✦ 点击星球，进入绘本世界 ✦</div>
      </div>
    </div>
  );
}
